// js/_shared/_confirm-modal.js
/* =============================================================================
   SHARED CONFIRM MODAL
   Path: /js/_shared/_confirm-modal.js
   
   Purpose: Reusable confirmation modal for delete/destructive actions.
            Provides a consistent UI with theme-aware styling.
   
   Dependencies: 
   - /js/_shared/_utils.js (load first)
   
   Namespace: window.AppUtils.ConfirmModal
   Global Alias: window.ConfirmModal (for convenience)
   
   Legacy Alias: window.Utils.showConfirmModal (backward compatibility)
   ========================================================================== */

window.AppUtils = window.AppUtils || {};

window.AppUtils.ConfirmModal = (function () {
    'use strict';

    /* =========================================================================
       PRIVATE VARIABLES
       ========================================================================= */

    var _modal = null;
    var _stylesInjected = false;
    var _currentOptions = null;

    /* =========================================================================
       STYLES
       ========================================================================= */

    var _styles = [
        '.confirm-modal {',
        '    position: fixed;',
        '    inset: 0;',
        '    z-index: 10001;',
        '    display: flex;',
        '    align-items: center;',
        '    justify-content: center;',
        '    animation: confirmFadeIn 0.2s ease-out;',
        '}',
        '.confirm-modal-backdrop {',
        '    position: absolute;',
        '    inset: 0;',
        '    background: rgba(0, 0, 0, 0.5);',
        '}',
        '.confirm-modal-dialog {',
        '    position: relative;',
        '    background: var(--r4o-surface);',
        '    border-radius: 12px;',
        '    padding: 24px;',
        '    max-width: 400px;',
        '    width: 90%;',
        '    text-align: center;',
        '    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);',
        '    animation: confirmSlideUp 0.2s ease-out;',
        '}',
        '.confirm-modal-icon {',
        '    width: 48px;',
        '    height: 48px;',
        '    border-radius: 50%;',
        '    display: flex;',
        '    align-items: center;',
        '    justify-content: center;',
        '    margin: 0 auto 16px;',
        '}',
        '.confirm-modal-icon--danger {',
        '    background: var(--r4o-danger-soft);',
        '    color: var(--r4o-danger);',
        '}',
        '.confirm-modal-icon--primary {',
        '    background: var(--r4o-info-soft);',
        '    color: var(--primary, #3b82f6);',
        '}',
        '.confirm-modal-icon--success {',
        '    background: var(--r4o-success-soft);',
        '    color: var(--r4o-green);',
        '}',
        '.confirm-modal-title {',
        '    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;',
        '    font-size: 18px;',
        '    font-weight: 600;',
        '    color: var(--r4o-text-strong);',
        '    margin: 0 0 8px;',
        '}',
        '.confirm-modal-message {',
        '    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;',
        '    font-size: 14px;',
        '    color: var(--r4o-text-muted);',
        '    margin: 0 0 24px;',
        '    line-height: 1.5;',
        '}',
        '.confirm-modal-actions {',
        '    display: flex;',
        '    gap: 12px;',
        '    justify-content: center;',
        '}',
        '.confirm-modal-btn {',
        '    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;',
        '    font-size: 14px;',
        '    font-weight: 500;',
        '    padding: 10px 20px;',
        '    border-radius: 8px;',
        '    border: none;',
        '    cursor: pointer;',
        '    transition: all 0.15s ease;',
        '    min-width: 100px;',
        '}',
        '.confirm-modal-btn--cancel {',
        '    background: var(--r4o-surface-muted-2);',
        '    color: var(--r4o-text-body);',
        '}',
        '.confirm-modal-btn--cancel:hover {',
        '    background: var(--r4o-border);',
        '}',
        '.confirm-modal-btn--confirm {',
        '    color: var(--r4o-white);',
        '}',
        '.confirm-modal-btn--danger {',
        '    background: var(--r4o-danger);',
        '}',
        '.confirm-modal-btn--danger:hover {',
        '    background: var(--r4o-danger-strong);',
        '}',
        '.confirm-modal-btn--primary {',
        '    background: var(--primary, #3b82f6);',
        '}',
        '.confirm-modal-btn--primary:hover {',
        '    filter: brightness(0.9);',
        '}',
        '.confirm-modal-btn--success {',
        '    background: var(--r4o-green);',
        '}',
        '.confirm-modal-btn--success:hover {',
        '    background: var(--r4o-success-strong);',
        '}',
        '@keyframes confirmFadeIn {',
        '    from { opacity: 0; }',
        '    to { opacity: 1; }',
        '}',
        '@keyframes confirmSlideUp {',
        '    from {',
        '        transform: translateY(20px);',
        '        opacity: 0;',
        '    }',
        '    to {',
        '        transform: translateY(0);',
        '        opacity: 1;',
        '    }',
        '}'
    ].join('\n');

    /* =========================================================================
       ICONS
       ========================================================================= */

    var _icons = {
        danger: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
            '<polyline points="3 6 5 6 21 6"></polyline>' +
            '<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>' +
            '</svg>',
        primary: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
            '<circle cx="12" cy="12" r="10"></circle>' +
            '<line x1="12" y1="16" x2="12" y2="12"></line>' +
            '<line x1="12" y1="8" x2="12.01" y2="8"></line>' +
            '</svg>',
        success: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
            '<path d="M22 2L11 13"></path>' +
            '<path d="M22 2L15 22L11 13L2 9L22 2Z"></path>' +
            '</svg>'
    };

    /* =========================================================================
       INITIALIZATION
       ========================================================================= */

    function _injectStyles() {
        if (_stylesInjected) return;

        var style = document.createElement('style');
        style.id = 'confirm-modal-styles';
        style.textContent = _styles;
        document.head.appendChild(style);
        _stylesInjected = true;
    }

    /* =========================================================================
       CORE FUNCTIONS
       ========================================================================= */

    /**
     * Show confirmation modal
     * @param {Object} options - Modal options
     * @param {string} [options.title='Confirm'] - Modal title
     * @param {string} [options.message='Are you sure?'] - Modal message
     * @param {string} [options.confirmText='Confirm'] - Confirm button text
     * @param {string} [options.cancelText='Cancel'] - Cancel button text
     * @param {string} [options.confirmClass='primary'] - 'primary', 'danger', or 'success'
     * @param {string} [options.htmlMessage] - Raw HTML message (bypasses escaping; use only with trusted content)
     * @param {Function} [options.onConfirm] - Callback when confirmed
     * @param {Function} [options.onCancel] - Callback when cancelled
     * @returns {Object} { close: Function } - API to programmatically close
     */
    function show(options) {
        options = options || {};

        _injectStyles();

        // Remove existing modal if present
        close();

        // Set options AFTER close() so they don't get nullified
        _currentOptions = options;

        var title = options.title || 'Confirm';
        var message = options.message || 'Are you sure?';
        var confirmText = options.confirmText || 'Confirm';
        var cancelText = options.cancelText || 'Cancel';
        var confirmClass = options.confirmClass || 'primary';
        var iconType = confirmClass === 'danger' ? 'danger' : (confirmClass === 'success' ? 'success' : 'primary');

        // Support raw HTML in message via htmlMessage option
        var messageHtml = options.htmlMessage || _escapeHtml(message);

        // Create modal
        _modal = document.createElement('div');
        _modal.id = 'sharedConfirmModal';
        _modal.className = 'confirm-modal';

        var hideCancel = options.hideCancel || false;
        var cancelHtml = hideCancel ? '' :
            '        <button class="confirm-modal-btn confirm-modal-btn--cancel">' + _escapeHtml(cancelText) + '</button>';

        _modal.innerHTML = [
            '<div class="confirm-modal-backdrop"></div>',
            '<div class="confirm-modal-dialog">',
            '    <div class="confirm-modal-icon confirm-modal-icon--' + iconType + '">',
            '        ' + _icons[iconType],
            '    </div>',
            '    <h3 class="confirm-modal-title">' + _escapeHtml(title) + '</h3>',
            '    <p class="confirm-modal-message">' + messageHtml + '</p>',
            '    <div class="confirm-modal-actions">',
            cancelHtml,
            '        <button class="confirm-modal-btn confirm-modal-btn--confirm confirm-modal-btn--' + confirmClass + '">' + _escapeHtml(confirmText) + '</button>',
            '    </div>',
            '</div>'
        ].join('\n');

        document.body.appendChild(_modal);

        // Bind events
        var backdrop = _modal.querySelector('.confirm-modal-backdrop');
        var cancelBtn = _modal.querySelector('.confirm-modal-btn--cancel');
        var confirmBtn = _modal.querySelector('.confirm-modal-btn--confirm');

        backdrop.addEventListener('click', _handleCancel);
        if (cancelBtn) cancelBtn.addEventListener('click', _handleCancel);
        confirmBtn.addEventListener('click', hideCancel ? _handleCancel : _handleConfirm);

        // Escape key
        document.addEventListener('keydown', _handleKeydown);

        // Focus confirm button when no cancel, otherwise cancel (safer default)
        (hideCancel ? confirmBtn : cancelBtn).focus();

        return {
            close: close
        };
    }

    /**
     * Escape HTML to prevent XSS
     * @param {string} str
     * @returns {string}
     */
    function _escapeHtml(str) {
        var div = document.createElement('div');
        div.textContent = str || '';
        return div.innerHTML;
    }

    /**
     * Handle confirm action
     */
    function _handleConfirm() {
        var callback = _currentOptions && _currentOptions.onConfirm;
        close();
        if (typeof callback === 'function') {
            callback();
        }
    }

    /**
     * Handle cancel action
     */
    function _handleCancel() {
        var callback = _currentOptions && _currentOptions.onCancel;
        close();
        if (typeof callback === 'function') {
            callback();
        }
    }

    /**
     * Handle keyboard events
     * @param {KeyboardEvent} e
     */
    function _handleKeydown(e) {
        if (e.key === 'Escape') {
            _handleCancel();
        }
    }

    /**
     * Close the modal
     */
    function close() {
        document.removeEventListener('keydown', _handleKeydown);

        // Remove the tracked modal
        if (_modal && _modal.parentNode) {
            _modal.parentNode.removeChild(_modal);
        }

        // Also remove any static modal with the same ID (from _ConfirmModal.cshtml partial)
        var existingModal = document.getElementById('sharedConfirmModal');
        if (existingModal && existingModal.parentNode) {
            existingModal.parentNode.removeChild(existingModal);
        }

        _modal = null;
        _currentOptions = null;
    }

    /* =========================================================================
       PUBLIC API
       ========================================================================= */

    return {
        show: show,
        close: close
    };

})();

/* =============================================================================
   GLOBAL ALIAS
   ========================================================================== */

window.ConfirmModal = window.AppUtils.ConfirmModal;

/* =============================================================================
   LEGACY ALIAS (Backward Compatibility)
   Keep window.Utils.showConfirmModal working for existing code
   ========================================================================== */

window.Utils = window.Utils || {};
window.Utils.showConfirmModal = window.AppUtils.ConfirmModal.show;